/**
 * API Client with timeout, error handling, and interceptors
 */
import { env } from '@config/env';
import type { RequestConfig } from '@/types/api.types';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestOptions {
  method?: HttpMethod;
  params?: Record<string, string | number | boolean | undefined>;
  data?: unknown;
  headers?: Record<string, string>;
  timeout?: number;
}

/**
 * Custom API Error class
 */
export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(status: number, message: string, data: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Get current language from localStorage
 * Falls back to 'ar' if not set
 */
function getCurrentLanguage(): string {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('locale');
    if (saved === 'ar' || saved === 'en') {
      return saved;
    }
  }
  return 'ar';
}

/**
 * API Client class with request deduplication
 */
class ApiClient {
  private baseURL: string;
  private timeout: number;
  private pendingRequests: Map<string, Promise<unknown>>;

  constructor() {
    this.baseURL = env.API_BASE_URL;
    this.timeout = env.API_TIMEOUT;
    this.pendingRequests = new Map();
  }

  /**
   * Get default headers with current language
   */
  private getDefaultHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      'Accept-Language': getCurrentLanguage(),
    };
  }

  /**
   * Generate a unique key for request deduplication
   */
  private getRequestKey(url: string, method: string): string {
    return `${method}:${url}`;
  }

  /**
   * Build URL with query parameters
   */
  private buildUrl(
    endpoint: string,
    params?: Record<string, string | number | boolean | undefined>
  ): string {
    let url = `${this.baseURL}${endpoint}`;

    if (params && Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    return url;
  }

  /**
   * Handle API response
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('content-type');
    const isJson = contentType?.includes('application/json');

    if (!response.ok) {
      const errorData = isJson ? await response.json().catch(() => null) : null;
      const errorMessage =
        errorData?.message || response.statusText || 'Request failed';
      throw new ApiError(response.status, errorMessage, errorData);
    }

    if (response.status === 204) {
      return null as T;
    }

    return isJson ? response.json() : response.text();
  }

  /**
   * Handle errors
   */
  private handleError(error: unknown): ApiError {
    if (error instanceof ApiError) {
      return error;
    }

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        return new ApiError(408, 'Request timeout');
      }

      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        return new ApiError(0, 'Network error - please check your connection');
      }

      return new ApiError(500, error.message || 'Unknown error occurred');
    }

    return new ApiError(500, 'Unknown error occurred');
  }

  /**
   * Execute the actual fetch request
   */
  private async executeRequest<T>(
    url: string,
    method: HttpMethod,
    options: {
      data?: unknown;
      headers: Record<string, string>;
      timeout: number;
    }
  ): Promise<T> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), options.timeout);

    const fetchOptions: RequestInit = {
      method,
      headers: {
        ...this.getDefaultHeaders(),
        ...options.headers,
      },
      signal: controller.signal,
    };

    if (options.data && method !== 'GET') {
      fetchOptions.body = JSON.stringify(options.data);
    }

    try {
      const response = await fetch(url, fetchOptions);
      clearTimeout(timeoutId);
      return await this.handleResponse<T>(response);
    } catch (error) {
      clearTimeout(timeoutId);
      throw this.handleError(error);
    }
  }

  /**
   * Make a request with deduplication for GET requests
   */
  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const {
      method = 'GET',
      params,
      data,
      headers = {},
      timeout = this.timeout,
    } = options;

    const url = this.buildUrl(endpoint, params);
    const requestKey = this.getRequestKey(url, method);

    // Deduplicate concurrent GET requests
    if (method === 'GET') {
      const pendingRequest = this.pendingRequests.get(requestKey);
      if (pendingRequest) {
        return pendingRequest as Promise<T>;
      }

      const request = this.executeRequest<T>(url, method, { data, headers, timeout })
        .finally(() => {
          this.pendingRequests.delete(requestKey);
        });

      this.pendingRequests.set(requestKey, request);
      return request;
    }

    // Non-GET requests are not deduplicated
    return this.executeRequest<T>(url, method, { data, headers, timeout });
  }

  /**
   * GET request
   */
  get<T>(endpoint: string, options: Omit<RequestOptions, 'method'> = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  /**
   * POST request
   */
  post<T>(
    endpoint: string,
    data?: unknown,
    options: Omit<RequestOptions, 'method' | 'data'> = {}
  ): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'POST', data });
  }

  /**
   * PUT request
   */
  put<T>(
    endpoint: string,
    data?: unknown,
    options: Omit<RequestOptions, 'method' | 'data'> = {}
  ): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PUT', data });
  }

  /**
   * PATCH request
   */
  patch<T>(
    endpoint: string,
    data?: unknown,
    options: Omit<RequestOptions, 'method' | 'data'> = {}
  ): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PATCH', data });
  }

  /**
   * DELETE request
   */
  delete<T>(endpoint: string, options: Omit<RequestOptions, 'method'> = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
